<template>
  <div class="blog-admin-form">
    <h3 class="mb-3">{{ isEditing ? 'Editar Entrada' : 'Crear Nueva Entrada' }}</h3>

    <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>
    <b-alert v-if="successMessage" show variant="success">{{ successMessage }}</b-alert>

    <form @submit.prevent="handleSubmit">
      <div class="mb-3">
        <label for="postTitle" class="form-label">Título <span class="text-danger">*</span></label>
        <input type="text" class="form-control" id="postTitle" v-model="formData.title" required @input="updateSlug">
      </div>

       <div class="mb-3">
        <label for="postSlug" class="form-label">Slug (auto-generado)</label>
        <input type="text" class="form-control" id="postSlug" v-model="formData.slug" readonly disabled>
         <small class="form-text text-muted">Se genera a partir del título.</small>
      </div>

      <div class="mb-3">
        <label for="postSummary" class="form-label">Resumen <span class="text-danger">*</span></label>
        <textarea class="form-control" id="postSummary" rows="3" v-model="formData.summary" required></textarea>
      </div>

      <div class="mb-3">
        <label for="postContent" class="form-label">Contenido <span class="text-danger">*</span></label>
        <!-- Integración de Quill Editor -->
        <QuillEditor
          ref="quillEditorRef"
          theme="snow"
          contentType="html"
          :content="formData.content"
          @update:content="formData.content = $event"
          placeholder="Escribe el contenido aquí..."
          style="min-height: 250px; background-color: white;"
          required
        />
         <small class="form-text text-muted">Usa el editor para formatear el texto.</small>
      </div>

      <div class="mb-3">
        <label for="listImage" class="form-label">Imagen para Listado (Opcional)</label>
        <select class="form-select" id="listImage" v-model="formData.list_image_id">
          <option :value="null">-- Seleccionar Imagen --</option>
          <option v-for="image in availableImages" :key="image.id" :value="image.id">
            {{ image.filename }} ({{ image.filepath }})
          </option>
        </select>
      </div>

      <div class="mb-3">
        <label for="detailImage" class="form-label">Imagen para Detalle (Opcional)</label>
        <select class="form-select" id="detailImage" v-model="formData.detail_image_id">
          <option :value="null">-- Seleccionar Imagen --</option>
          <option v-for="image in availableImages" :key="image.id" :value="image.id">
            {{ image.filename }} ({{ image.filepath }})
          </option>
        </select>
      </div>

      <div class="form-check mb-3">
        <input class="form-check-input" type="checkbox" id="publishNow" v-model="formData.publish_now">
        <label class="form-check-label" for="publishNow">
          Publicar inmediatamente (si no, se guarda como borrador)
        </label>
      </div>

      <div class="d-flex justify-content-end">
         <b-button type="button" variant="secondary" @click="cancelEdit" class="me-2" v-if="isEditing">
           Cancelar Edición
         </b-button>
         <b-button type="submit" :variant="isEditing ? 'success' : 'primary'" :disabled="loading">
           <b-spinner small v-if="loading"></b-spinner>
           {{ isEditing ? 'Actualizar Entrada' : 'Crear Entrada' }}
         </b-button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, reactive, watch, defineProps, defineEmits, nextTick, onMounted } from 'vue';
import { BAlert, BButton, BSpinner } from 'bootstrap-vue-next';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import { createBlogPost, updateBlogPost } from '../../../services/blogService.js';
import { listImages } from '../../../services/imageService.js';

const props = defineProps({
  postToEdit: {
    type: Object,
    default: null
  },
  availableImages: {
    type: Array,
    default: () => []
  },
});

const emit = defineEmits(['post-saved', 'cancel-edit']);

const isEditing = ref(false);
const loading = ref(false);
const error = ref(null);
const successMessage = ref(null);
const quillEditorRef = ref(null);

const initialFormData = {
  id: null,
  title: '',
  slug: '',
  summary: '',
  content: '',
  list_image_id: null,
  detail_image_id: null,
  publish_now: false,
};

const formData = reactive({ ...initialFormData });

function slugify(text) {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
}

function updateSlug() {
    formData.slug = slugify(formData.title);
}

watch(() => props.postToEdit, async (newPost) => {
  if (newPost && newPost.id) {
    isEditing.value = true;
    formData.id = newPost.id;
    formData.title = newPost.title || '';
    formData.slug = newPost.slug || slugify(newPost.title);
    formData.summary = newPost.summary || '';
    formData.content = newPost.content || '';
    formData.list_image_id = newPost.list_image_id ? parseInt(newPost.list_image_id, 10) : null;
    formData.detail_image_id = newPost.detail_image_id ? parseInt(newPost.detail_image_id, 10) : null;
    // console.log('Editando post:', newPost); // Eliminado
    // console.log('Asignando list_image_id:', formData.list_image_id, 'desde', newPost.list_image_id); // Eliminado
    // console.log('Asignando detail_image_id:', formData.detail_image_id, 'desde', newPost.detail_image_id); // Eliminado
    // console.log('availableImages en BlogAdminForm:', props.availableImages); // Eliminado
    formData.publish_now = !!newPost.published_at;
    error.value = null;
    successMessage.value = null;

    await nextTick();

    if (quillEditorRef.value) {
      const quill = quillEditorRef.value.getQuill();
      if (quill) {
        const currentHTML = quill.root.innerHTML;
        if (currentHTML !== formData.content) {
           quill.root.innerHTML = formData.content;
        }
      } else {
         // console.warn("BlogAdminForm: Instancia de Quill no encontrada en la ref al intentar establecer contenido."); // Eliminado
      }
    } else {
       // console.warn("BlogAdminForm: Ref de Quill no disponible al intentar establecer contenido."); // Eliminado
    }

  } else {
    resetForm();
  }
}, { immediate: true });

// Watcher para actualizar los selectores cuando las imágenes estén disponibles
watch(() => props.availableImages, (newImages) => {
  if (newImages && newImages.length > 0) {
    // Forzar una pequeña actualización para que Vue reactive los selectores
    // Esto puede ser un hack, pero a veces necesario con selectores dinámicos
    const currentListImageId = formData.list_image_id;
    const currentDetailImageId = formData.detail_image_id;
    formData.list_image_id = null; // Resetear temporalmente
    formData.detail_image_id = null; // Resetear temporalmente
    nextTick(() => {
      formData.list_image_id = currentListImageId; // Restaurar valor
      formData.detail_image_id = currentDetailImageId; // Restaurar valor
    });
  }
});


function resetForm() {
    Object.assign(formData, initialFormData);
    isEditing.value = false;
    error.value = null;
    successMessage.value = null;
    nextTick(() => {
        if (quillEditorRef.value) {
            const quill = quillEditorRef.value.getQuill();
            if (quill) {
                quill.root.innerHTML = '';
            }
        }
    });
}

function cancelEdit() {
    resetForm();
    emit('cancel-edit');
}

async function handleSubmit() {
  loading.value = true;
  error.value = null;
  successMessage.value = null;

  // No se necesita verificar localAdminPassword aquí, ya viene como prop

  if (formData.title && !formData.slug) {
      updateSlug();
  }

  const dataToSend = {
      title: formData.title,
      slug: formData.slug,
      summary: formData.summary,
      content: formData.content,
      list_image_id: formData.list_image_id,
      detail_image_id: formData.detail_image_id,
      publish_now: formData.publish_now,
  };

  if (isEditing.value && formData.id) {
      dataToSend.id = formData.id;
  }

  try {
    let response;
    const token = localStorage.getItem('jwt_token'); // Obtener el token JWT
    if (!token) {
        error.value = "No autenticado. Por favor, inicia sesión de nuevo.";
        loading.value = false;
        return;
    }

    if (isEditing.value) {
      response = await updateBlogPost(formData.id, dataToSend, token); // Usar token
      successMessage.value = `Entrada "${formData.title}" actualizada correctamente.`;
    } else {
      response = await createBlogPost(dataToSend, token); // Usar token
      successMessage.value = `Entrada "${formData.title}" creada correctamente (ID: ${response.id}).`;
      resetForm();
      window.scrollTo(0, 0); // Desplazar al principio de la página
    }
    emit('post-saved');
  } catch (err) {
    // console.error("Error al guardar la entrada:", err); // Eliminado
    error.value = `Error: ${err.message}`;
  } finally {
    loading.value = false;
    setTimeout(() => {
        successMessage.value = null;
        error.value = null;
    }, 5000);
  }
}

</script>

<style>
.ql-editor {
  min-height: 200px;
}
.blog-admin-form {
    background-color: #f8f9fa;
    padding: 2rem;
    border-radius: 0.5rem;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}
</style>